import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { NOTES } from "../../lib/chords";
import { capturePointer } from "../../lib/pointer";

const KEYMAP = "awsedftgyhujkolp;";
const BLACK = [1, 3, 6, 8, 10];

export const Keyboard = ({ inst, kind, baseOctave, disabled, active }) => {
  const [oct, setOct] = useState(baseOctave);
  const [held, setHeld] = useState([]);
  const down = useRef(new Set());
  const base = (oct + 1) * 12;
  const midis = Array.from({ length: 25 }, (_, i) => base + i);
  const whites = midis.filter((m) => !BLACK.includes(m % 12));
  const w = 100 / whites.length;

  const on = useCallback((m) => { if (disabled) return; inst.ctx.resume(); inst.noteOn(kind, m); setHeld((h) => [...h, m]); }, [inst, kind, disabled]);
  const off = useCallback((m) => { inst.noteOff(kind, m); setHeld((h) => h.filter((x) => x !== m)); }, [inst, kind]);

  useEffect(() => {
    if (!active) return;
    const skip = (e) => ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) || e.metaKey || e.ctrlKey;
    const kd = (e) => {
      const i = KEYMAP.indexOf(e.key.toLowerCase());
      if (i < 0 || skip(e) || e.repeat || down.current.has(i)) return;
      down.current.add(i);
      on(base + i);
    };
    const ku = (e) => {
      const i = KEYMAP.indexOf(e.key.toLowerCase());
      if (i < 0 || !down.current.has(i)) return;
      down.current.delete(i);
      off(base + i);
    };
    const held_ = down.current;
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    return () => { window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); held_.clear(); inst.allOff(); };
  }, [active, base, on, off, inst]);

  const label = (m) => `${NOTES[m % 12]}${Math.floor(m / 12) - 1}`;
  const keyProps = (m) => ({
    onPointerDown: (e) => { e.preventDefault(); capturePointer(e.currentTarget, e.pointerId); on(m); },
    onPointerUp: () => off(m),
    onPointerCancel: () => off(m),
    "data-testid": `${kind}-key-${label(m).replace("#", "s")}`,
  });

  return (
    <div data-testid={`keyboard-${kind}`}>
      <div className="mb-3 flex items-center gap-3">
        <button onClick={() => setOct((o) => Math.max(1, o - 1))} data-testid={`${kind}-octave-down-btn`} className="rounded-[3px] border border-zinc-700 p-1.5 text-zinc-300 hover:border-yellow-400"><ChevronLeft className="h-4 w-4" /></button>
        <span className="mono text-xs text-zinc-300" data-testid={`${kind}-octave-value`}>Octave {oct}</span>
        <button onClick={() => setOct((o) => Math.min(6, o + 1))} data-testid={`${kind}-octave-up-btn`} className="rounded-[3px] border border-zinc-700 p-1.5 text-zinc-300 hover:border-yellow-400"><ChevronRight className="h-4 w-4" /></button>
        <span className="mono text-[10px] text-zinc-500 hidden md:inline">Computer keys: A W S E D F T G Y H U J K</span>
      </div>
      <div className="relative h-36 select-none touch-none">
        {whites.map((m, i) => (
          <button key={m} {...keyProps(m)} style={{ left: `${i * w}%`, width: `${w}%` }}
            className={`absolute top-0 h-full rounded-b-[3px] border border-zinc-900 transition-colors ${held.includes(m) ? "bg-yellow-300" : "bg-zinc-100 hover:bg-white"}`}>
            {m % 12 === 0 && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 mono text-[9px] text-zinc-500">{label(m)}</span>}
          </button>
        ))}
        {midis.filter((m) => BLACK.includes(m % 12)).map((m) => {
          const before = whites.filter((x) => x < m).length;
          return (
            <button key={m} {...keyProps(m)} style={{ left: `${before * w - w * 0.3}%`, width: `${w * 0.6}%` }}
              className={`absolute top-0 z-10 h-[60%] rounded-b-[3px] border border-black transition-colors ${held.includes(m) ? "bg-pink-500" : "bg-zinc-900 hover:bg-zinc-700"}`} />
          );
        })}
      </div>
    </div>
  );
};
